import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, PencilLine, History, Settings, Users, LogOut, Menu, X, Users2, Fuel } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Panel", icon: LayoutDashboard, roles: ["admin", "editor", "viewer"], testid: "nav-dashboard" },
  { to: "/entry", label: "Veri Girişi", icon: PencilLine, roles: ["admin", "editor"], testid: "nav-entry" },
  { to: "/history", label: "Kayıt Geçmişi", icon: History, roles: ["admin", "editor", "viewer"], testid: "nav-history" },
  { to: "/personel", label: "Personel & Mesai", icon: Users2, roles: ["admin", "editor", "viewer"], testid: "nav-personnel" },
  { to: "/fuel", label: "Yakıt Analizi", icon: Fuel, roles: ["admin", "editor", "viewer"], testid: "nav-fuel" },
  { to: "/settings", label: "Planlanan Metrajlar", icon: Settings, roles: ["admin"], testid: "nav-settings" },
  { to: "/users", label: "Kullanıcılar", icon: Users, roles: ["admin"], testid: "nav-users" },
];

const roleLabels = {
  admin: "Yönetici",
  editor: "Editör",
  viewer: "Görüntüleyici",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    toast.success("Çıkış yapıldı");
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  const availableLinks = links.filter((l) => l.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky lg:top-0 lg:h-screen lg:self-start inset-y-0 lg:inset-y-auto left-0 z-40 w-72 bg-white border-r border-slate-200 flex flex-col
          transform transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-3">
          <Logo size={40} showWordmark />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {availableLinks.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-sky-50 text-sky-600 border-l-4 border-sky-500"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
              data-testid={testid}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-3 bg-slate-50 rounded-md mb-2">
            <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-900 truncate" data-testid="user-name">{user.name}</div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-sky-500" data-testid="user-role">
                {roleLabels[user.role]}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full border-2 border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900 font-semibold"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <LogOut size={16} className="mr-2" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Logo size={32} />
          <div className="font-display font-black text-sm text-slate-900">ATEŞ İNŞAAT</div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </Button>
      </div>

      {/* Backdrop for mobile */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/40 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0" data-testid="main-content">
        <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
